import React, { useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Nav, Navbar, Row, Col } from 'react-bootstrap';
import { navbarBreakPoint, topNavbarBreakpoint } from 'config';
import Flex from 'components/common/Flex';
import Logo from 'components/common/Logo';
import NavbarVerticalMenu from './NavbarVerticalMenu';
import ToggleButton from './ToggleButton';
import routes, { peltaskRoutes, peltaskAdminRoutes, pharmacyAdminRoutes, reportRoutes, Rout4MeRoutes, orderHistoryRoutes, patientRoutes, masterRoutes, MedpassRoutes, drugListRoute, Resources, caregiverRoutes } from 'routes/siteMaps';
import { capitalize } from 'helpers/utils';
import NavbarTopDropDownMenus from 'components/navbar/top/NavbarTopDropDownMenus';
import PurchaseCard from './PurchaseCard';
import bgNavbar from 'assets/img/generic/bg-navbar.png';
import { useAppContext } from 'providers/AppProvider';
import { ACCESS_CONTROL } from 'components/authentication/accessControlMap'
import { isCaregiverRole } from 'helpers/isSharedLivingActive'

const getDayPrograms = () => {
  let localStorageAccessControl = localStorage.getItem("accessControl");
  const dayprogramActive = localStorageAccessControl.includes(ACCESS_CONTROL["dayprogramactive"])
  const dayProgramInLocalStorage = JSON.parse(localStorage.getItem('dayPrograms'));
  try {
    const dayPrograms = ((dayProgramInLocalStorage && dayprogramActive) || []);
    return dayPrograms ? dayProgramInLocalStorage : [];
  } catch (error) {
    return [];
  }
};

const getPatientRoutesForRole = () => {
  const roleId = Number(localStorage.getItem("roleId"));
  let localStorageAccessControl = localStorage.getItem("accessControl");
  const dayprogramActive = localStorageAccessControl.includes(ACCESS_CONTROL["dayprogramactive"])
  const hasDayPrograms = ((roleId == 4) && dayprogramActive) ? true : (dayprogramActive && getDayPrograms().length) > 0;

  return {
    ...patientRoutes,
    children: patientRoutes.children.filter(route => {
      if (route.name === 'View Day Program Patients') {
        return hasDayPrograms;
      }

      if (roleId === 6) {
        return false;
      }

      return true;
    })
  };
};

const getMasterRoutesForRole = roleId => ({
  ...masterRoutes,
  children: masterRoutes.children.map(route => {
    if (route.name === 'User Management') {
      return {
        ...route,
        children: route.children.filter(child =>
          child.name === 'All Active Sessions' ? roleId < 4 : true
        )
      };
    }

    if (route.name === 'Admin') {
      let localStorageAccessControl = localStorage.getItem("accessControl");
      const dayprogramActive = localStorageAccessControl.includes(ACCESS_CONTROL["dayprogramactive"])
      return {
        ...route,
        children: route.children.filter(child => {
          // Bulk Import: System Admin (1) and Pharmacy Admin (2) only.
          if (child.name === 'Bulk Import') return roleId === 1 || roleId === 2;
          child.name === 'Day Programs' ? (roleId <= 4 && dayprogramActive) : true
          return true;
      })
      };
    }

    return route;
  })
});

const dynamicRouteLabels = new Set([
  patientRoutes.label,
  masterRoutes.label,
  reportRoutes.label,
  peltaskRoutes.label,
  peltaskAdminRoutes.label,
  pharmacyAdminRoutes.label,
  orderHistoryRoutes.label,
  MedpassRoutes.label,
  Resources.label
]);

const getBaseRoutes = () =>
  routes.filter(route => !dynamicRouteLabels.has(route.label));

const NavbarVertical = () => {
  const {
    config: {
      navbarPosition,
      navbarStyle,
      isNavbarVerticalCollapsed,
      showBurgerMenu
    }
  } = useAppContext();
  
let localStorageAccessControl = localStorage.getItem("accessControl");
if (localStorageAccessControl === null) {
  localStorageAccessControl = [];
} else {
  localStorageAccessControl = localStorageAccessControl.split(",");
}


  const HTMLClassList = document.getElementsByTagName('html')[0].classList;

  const roleId = Number(localStorage.getItem("roleId"));
  const configuredPatientRoutes = getPatientRoutesForRole();
  const navRoutes = getBaseRoutes();

  // SharedLiving caregiver (role 7): tightly scoped sidebar — Dashboard, My
  // Patients, Reports, MedPass, Resources only. No admin / cycles / orders /
  // day program / facility settings (UI-033).
  if (isCaregiverRole()) {
    const caregiverNav = [...getBaseRoutes(), caregiverRoutes];
    return (
      <Navbar
        expand={navbarBreakPoint}
        className={classNames('navbar-vertical', {
          [`navbar-${navbarStyle}`]: navbarStyle !== 'transparent'
        })}
        variant="dark"
      >
        <Flex alignItems="center">
          <ToggleButton />
          <Logo at="navbar-vertical" textClass="text-primary" width={160} />
        </Flex>
        <Navbar.Collapse
          in={showBurgerMenu}
          style={{
            backgroundImage:
              navbarStyle === 'vibrant'
                ? `linear-gradient(-45deg, rgba(0, 160, 255, 0.86), #0048a2),url(${bgNavbar})`
                : 'none'
          }}
        >
          <div className="navbar-vertical-content scrollbar">
            <Nav className="flex-column" as="ul">
              {caregiverNav.map(route => (
                <Fragment key={route.label}>
                  {!route.labelDisable && (
                    <Nav.Item as="li">
                      <Row className="mt-3 mb-2 navbar-vertical-label-wrapper">
                        <Col xs="auto" className="navbar-vertical-label navbar-vertical-label">
                          {capitalize(route.label)}
                        </Col>
                        <Col className="ps-0">
                          <hr className="mb-0 navbar-vertical-divider"></hr>
                        </Col>
                      </Row>
                    </Nav.Item>
                  )}
                  <NavbarVerticalMenu routes={route.children} />
                </Fragment>
              ))}
            </Nav>
          </div>
        </Navbar.Collapse>
      </Navbar>
    );
  }

  if(roleId !== 3 && configuredPatientRoutes.children.length > 0){
    navRoutes.splice(1, 0, configuredPatientRoutes);
  }
  if(localStorage.getItem("roleId") != '3' && localStorage.getItem("roleId") != '5' && localStorage.getItem("roleId") != '6'){
    navRoutes.push(getMasterRoutesForRole(roleId));
  }

  if(localStorageAccessControl.includes(String(ACCESS_CONTROL["mym"])) && localStorage.getItem("roleId") != '6'){
    navRoutes.push(reportRoutes);
  }

  if (localStorage.getItem("roleId") >= 4 && Number(localStorage.getItem("roleId")) != 6) {
    navRoutes.push(peltaskRoutes);
    navRoutes.push(orderHistoryRoutes);
  }
  else if(localStorage.getItem("roleId") == 2){
    navRoutes.push(pharmacyAdminRoutes);
    // navRoutes.push(Rout4MeRoutes); TODO comented for now
    navRoutes.push(peltaskAdminRoutes);
  }
  else if(Number(localStorage.getItem("roleId")) != 6) {
    navRoutes.push(peltaskAdminRoutes);
  }

  if (Number(localStorage.getItem("roleId")) == 4 || Number(localStorage.getItem("roleId")) == 5) {
    if (!navRoutes.includes(MedpassRoutes)){
      navRoutes.push(MedpassRoutes);
    }
  }
    navRoutes.push(Resources);

  useEffect(() => {
    if (isNavbarVerticalCollapsed) {
      HTMLClassList.add('navbar-vertical-collapsed');
    } else {
      HTMLClassList.remove('navbar-vertical-collapsed');
    }
    return () => {
      HTMLClassList.remove('navbar-vertical-collapsed-hover');
    };
  }, [isNavbarVerticalCollapsed, HTMLClassList]);

  //Control mouseEnter event
  let time = null;
  // const handleMouseEnter = () => {
  //   if (isNavbarVerticalCollapsed) {
  //     time = setTimeout(() => {
  //       HTMLClassList.add('navbar-vertical-collapsed-hover');
  //     }, 100);
  //   }
  // };
  // const handleMouseLeave = () => {
  //   clearTimeout(time);
  //   HTMLClassList.remove('navbar-vertical-collapsed-hover');
  // };

  const NavbarLabel = ({ label }) => (
    <Nav.Item as="li">
      <Row className="mt-3 mb-2 navbar-vertical-label-wrapper">
        <Col xs="auto" className="navbar-vertical-label navbar-vertical-label">
          {label}
        </Col>
        <Col className="ps-0">
          <hr className="mb-0 navbar-vertical-divider"></hr>
        </Col>
      </Row>
    </Nav.Item>
  );

  return (
    <Navbar
      expand={navbarBreakPoint}
      className={classNames('navbar-vertical', {
        [`navbar-${navbarStyle}`]: navbarStyle !== 'transparent'
      })}
      variant="dark"
    >
      <Flex alignItems="center">
        <ToggleButton />
        <Logo at="navbar-vertical" textClass="text-primary" width={160} />
      </Flex>
      <Navbar.Collapse
        in={showBurgerMenu}
        // onMouseEnter={handleMouseEnter}
        // onMouseLeave={handleMouseLeave}
        style={{
          backgroundImage:
            navbarStyle === 'vibrant'
              ? `linear-gradient(-45deg, rgba(0, 160, 255, 0.86), #0048a2),url(${bgNavbar})`
              : 'none'
        }}
      >
        <div className="navbar-vertical-content scrollbar">
          <Nav className="flex-column" as="ul">
            {navRoutes.map(route => (
              <Fragment key={route.label}>
                {!route.labelDisable && (
                  <NavbarLabel label={capitalize(route.label)} />
                )}
                <NavbarVerticalMenu routes={route.children} />
              </Fragment>
            ))}
          </Nav>

          <>
            {navbarPosition === 'combo' && (
              <div className={`d-${topNavbarBreakpoint}-none`}>
                <div className="navbar-vertical-divider">
                  <hr className="navbar-vertical-hr my-2" />
                </div>
                <Nav navbar>
                  <NavbarTopDropDownMenus />
                </Nav>
              </div>
            )}
          </>
        </div>
      </Navbar.Collapse>
    </Navbar>
  );
};

NavbarVertical.propTypes = {
  label: PropTypes.string
};

export default NavbarVertical;
